import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../Logo';
import {
  ChartPieSlice,
  ChartLineUp,
  Plus,
  FileText,
  IdentificationCard,
  Receipt,
  Calculator,
  Coins,
  Calendar,
  Broom,
  MapPin,
  QrCode,
  Ticket,
  UserCircle,
  Buildings,
  CreditCard,
  Bell,
  Crown,
  Signature,
  X,
} from '@phosphor-icons/react';

const groups = (isAdmin) => [
  {
    title: 'Overview',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: ChartPieSlice },
      { to: '/statistics', label: 'Statistiche', icon: ChartLineUp },
    ],
  },
  {
    title: 'Gestione Ospiti',
    items: [
      { to: '/new-contract', label: 'Nuovo Contratto', icon: Plus },
      { to: '/templates', label: 'Modelli Contratto', icon: FileText },
      { to: '/firma', label: 'Firma Host', icon: Signature },
    ],
  },
  {
    title: 'Fiscale',
    items: [
      { to: '/receipts', label: 'Ricevute Affitto', icon: Receipt },
      { to: '/ricevute-imposta-soggiorno', label: 'Ricevute Imposta Soggiorno', icon: Coins },
      { to: '/contabilita', label: 'Contabilità', icon: Calculator },
      { to: '/imposta-soggiorno', label: 'Imposta Soggiorno', icon: Coins },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/pulizie', label: 'Pulizie', icon: Broom },
      { to: '/eventi', label: 'Eventi Locali', icon: MapPin },
      { to: '/qrcode', label: 'QR Code', icon: QrCode },
    ],
  },
  {
    title: 'Impostazioni',
    items: [
      { to: '/properties', label: 'Proprietà', icon: Buildings },
      { to: '/firma', label: 'Firma & Sicurezza', icon: Signature },
      { to: '/subscription', label: 'Abbonamento', icon: CreditCard },
      { to: '/notifications', label: 'Notifiche', icon: Bell },
      ...(isAdmin ? [{ to: '/admin', label: 'Pannello Admin', icon: Crown, highlight: true }] : []),
    ],
  },
];

export default function Sidebar({ onNavigate = () => {} }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  return (
    <aside
      className="w-[248px] h-full bg-[#FEFDFB] border-r border-[#EEEAE0] flex flex-col"
      data-testid="app-sidebar"
    >
      {/* Logo */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <button
          onClick={() => { navigate('/dashboard'); onNavigate(); }}
          className="flex items-center"
          data-testid="sidebar-logo-btn"
        >
          <Logo size="lg" />
        </button>
      </div>

      {/* User compact */}
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[#FAF7F2] border border-[#EEEAE0]">
          <UserCircle size={26} weight="fill" className="text-[#0d9488] flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-zinc-900 truncate">{user?.name || 'Host'}</p>
            <p className="text-[11px] text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto px-3 pb-6" data-testid="sidebar-nav">
        {groups(isAdmin).map((g) => (
          <div key={g.title}>
            <p className="sidebar-group-title">{g.title}</p>
            {g.items.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={onNavigate}
                  end
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''} ${it.highlight ? 'text-amber-700' : ''}`
                  }
                  data-testid={`sidebar-link-${it.to.replace('/', '')}`}
                >
                  <Icon size={18} weight={it.highlight ? 'fill' : 'regular'} />
                  <span className="truncate">{it.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-[#EEEAE0] text-[11px] text-zinc-400 text-center">
        Locazione Turistica · v2026
      </div>
    </aside>
  );
}

export function MobileSidebarClose({ onClose }) {
  return (
    <button
      onClick={onClose}
      className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100"
      data-testid="sidebar-close-btn"
      aria-label="Chiudi menu"
    >
      <X size={22} />
    </button>
  );
}
